import axios from 'axios'

const TOOLS_API_BASE_URL = 'https://api.ouyiuri.com'
const TOOLS_API_TOKEN =
  import.meta.env.VITE_TOOLS_API_TOKEN ||
  'spat_OTRMEUct-D6K8BqnZ7YT43WkDwtDE-dpbaJ_5KMIQP0'

const getToolsHeaders = (shopifyId: string): Record<string, string> => ({
  'Content-Type': 'application/json',
  'shopify-id': shopifyId,
  Authorization: `Bearer ${TOOLS_API_TOKEN}`,
  'X-Api-Token': TOOLS_API_TOKEN,
})

export type TemplateEnv = 'dev' | 'prod'

export type TemplateVersionStatus = {
  shopifyId: string
  env: TemplateEnv
  currentVersion: string
  appliedVersion: string
  /** true = already on latest; false = not latest, update needed */
  isNewVersion: boolean
  assetRuntime?: string
  themeId?: number
  status?: string
  appliedAt?: string
}

export type ApplyNewTemplatePayload = {
  source: 'n' | 'p' | 'node' | 'php'
  env: TemplateEnv
  dryRun?: boolean
  forceApply?: boolean
  themeId?: number
  mapping?: Record<string, unknown>
}

type ApiResponse<T> = {
  code: number
  message: string
  data: T
}

/** Map app env to tools API env (dev | prod). */
export const getTemplateEnv = (): TemplateEnv => {
  if (import.meta.env.PROD) return 'prod'
  return 'dev'
}

const assertOk = <T>(response: { status: number; data: ApiResponse<T> }, fallbackMsg: string) => {
  const payload = response.data
  const code = payload?.code ?? response.status
  if (code !== 200) {
    const error = new Error(payload?.message || fallbackMsg) as Error & { code: number }
    error.code = code
    throw error
  }
  return payload.data
}

export const getApplyNewTemplateStatus = async (shopifyId: string, env?: TemplateEnv) => {
  const templateEnv = env || getTemplateEnv()
  const response = await axios.get<ApiResponse<TemplateVersionStatus>>(
    `${TOOLS_API_BASE_URL}/api/tools/shopify/shops/${encodeURIComponent(shopifyId)}/apply-new-template/status`,
    {
      params: { env: templateEnv },
      headers: getToolsHeaders(shopifyId),
      validateStatus: () => true,
    },
  )
  return assertOk(response, 'Failed to check template version')
}

export const applyNewTemplate = async (
  shopifyId: string,
  payload?: Partial<ApplyNewTemplatePayload>,
) => {
  const body: ApplyNewTemplatePayload = {
    source: 'p',
    env: getTemplateEnv(),
    dryRun: false,
    forceApply: true,
    ...payload,
  }

  const response = await axios.post<ApiResponse<unknown>>(
    `${TOOLS_API_BASE_URL}/api/tools/shopify/shops/${encodeURIComponent(shopifyId)}/apply-new-template`,
    body,
    {
      headers: getToolsHeaders(shopifyId),
      timeout: 120000,
      validateStatus: () => true,
    },
  )
  return assertOk(response, 'Failed to apply new template')
}
